import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { api, buildQueryString } from '@/lib/api'

/**
 * Map page overlay surfacing ransomware leak-site claim density per
 * country (Plan B Plan 2 T16/T17, spec §5.4).
 *
 * Plan T17 Step 3: "Verify the existing map already has a
 * `natural-earth-countries` source — if not, log Open Issue + render a
 * simple per-country list overlay instead." The current `MapNextPage` SOC
 * composition (Globe / RegionMap / CountryMap) does NOT expose a
 * MapLibre instance with a `natural-earth-countries` source, so we render
 * the choropleth as a list overlay panel inside the map main area. A
 * follow-up can swap this for a true MapLibre layer once the map page is
 * unified onto a single MapLibre instance.
 *
 * Wraps `GET /api/leak-sites/geo-density?from=&to=` (T7).
 */

interface GeoCellTopGroup {
  id: string
  name: string
  count: number
}

interface GeoCell {
  countryIso2: string
  claimCount: number
  topGroups: GeoCellTopGroup[]
}

interface GeoDensityResponse {
  cells: GeoCell[]
}

export interface RansomwareActivityOverlayProps {
  enabled: boolean
  fromTo: { from?: string; to?: string }
}

export function RansomwareActivityOverlay({
  enabled,
  fromTo,
}: RansomwareActivityOverlayProps) {
  const [selected, setSelected] = useState<GeoCell | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['leak-sites', 'geo-density', fromTo.from ?? '', fromTo.to ?? ''],
    queryFn: () =>
      api.get<GeoDensityResponse>(
        `/leak-sites/geo-density${buildQueryString({
          from: fromTo.from,
          to: fromTo.to,
        })}`,
      ),
    enabled,
    staleTime: 60_000,
    retry: false,
  })

  const sortedCells = useMemo(() => {
    if (!data?.cells) return []
    return [...data.cells].sort((a, b) => b.claimCount - a.claimCount)
  }, [data])

  if (!enabled) return null

  const maxClaims = sortedCells[0]?.claimCount ?? 0

  return (
    <>
      <aside
        aria-label="Ransomware activity by country"
        className="pointer-events-auto absolute left-3 top-3 z-30 flex max-h-[calc(100%-1.5rem)] w-72 min-h-0 flex-col overflow-hidden rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-secondary)] shadow-lg"
      >
        <header className="flex items-center justify-between border-b border-[color:var(--border-subtle)] px-3 py-2">
          <h2 className="text-sm font-semibold">Ransomware Activity</h2>
          <span className="text-xs text-muted-foreground">by country</span>
        </header>
        <div className="flex-1 overflow-y-auto p-2">
          {isLoading ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : isError ? (
            <p className="text-xs text-muted-foreground">No data available.</p>
          ) : sortedCells.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No ransomware claims in this window.
            </p>
          ) : (
            <ul className="space-y-1">
              {sortedCells.map((cell) => (
                <li key={cell.countryIso2}>
                  <button
                    type="button"
                    onClick={() => setSelected(cell)}
                    className="group flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1 text-left text-sm hover:bg-[color:var(--surface-tertiary)] focus:outline-none focus:ring-1 focus:ring-primary"
                    aria-label={`${cell.countryIso2} — ${cell.claimCount} claim${cell.claimCount === 1 ? '' : 's'}`}
                  >
                    <span className="flex items-center gap-2">
                      <span
                        aria-hidden
                        className="inline-block h-2 w-8 rounded-sm"
                        style={{
                          backgroundColor: heatColor(cell.claimCount, maxClaims),
                        }}
                      />
                      <span className="font-mono text-xs">
                        {cell.countryIso2}
                      </span>
                    </span>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {cell.claimCount}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>

      {selected && (
        <div
          role="dialog"
          aria-modal="false"
          aria-label={`${selected.countryIso2} ransomware activity detail`}
          className="pointer-events-auto absolute right-3 top-3 z-30 w-80 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-secondary)] p-3 shadow-lg"
        >
          <div className="mb-2 flex items-start justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold">
                {selected.countryIso2} — {selected.claimCount} claim
                {selected.claimCount === 1 ? '' : 's'}
              </h3>
              <p className="text-xs text-muted-foreground">Top groups</p>
            </div>
            <button
              type="button"
              onClick={() => setSelected(null)}
              className="rounded-sm p-1 hover:bg-[color:var(--surface-tertiary)]"
              aria-label="Close detail"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
          {selected.topGroups.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No group breakdown available.
            </p>
          ) : (
            <ol className="space-y-1">
              {selected.topGroups.map((g) => (
                <li
                  key={g.id}
                  className="flex items-baseline justify-between text-sm"
                >
                  <span>{g.name}</span>
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {g.count}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </>
  )
}

/**
 * Linear interpolation between three stops mirroring the spec's
 * MapLibre paint expression (transparent → amber → orange → red) so the
 * list rows convey the same heat signal as a true choropleth would.
 */
function heatColor(count: number, max: number): string {
  if (count === 0 || max === 0) return 'transparent'
  const ratio = Math.min(1, count / Math.max(max, 1))
  if (ratio < 0.33) return '#fde68a' // amber-200
  if (ratio < 0.66) return '#f97316' // orange-500
  return '#b91c1c' // red-700
}
